import { Box, Flex, Icon, IconButton, Text } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IconCopy } from 'assets/icons';
import Button from 'components/Button';
import { AnimatedMascot } from 'components/Mascot';
import Tooltip from 'components/Tooltip';
import { useFetchZapierApiKey } from 'hooks/integrations/useFetchZapierApiKey';
import { useRevokeZapierApiKey } from 'hooks/integrations/useRevokeZapierApiKey';
import { useIntegrationStore } from 'store/useIntegrationStore';
import toaster from 'utils/toaster';

const Configuration = () => {
  const [enable, setEnable] = useState<boolean>(false);
  const [apiKey, setApiKey] = useState<string>('');

  const { t } = useTranslation(['integrations']);

  const { status: generateKeyStatus, data: generateKeyData, refetch } = useFetchZapierApiKey();
  const { status: revokeStatus, mutateAsync: revokeMutateAsync } = useRevokeZapierApiKey();

  const integrationListData: any = useIntegrationStore(state => state.integrationList);
  const zapierIntegrationData = integrationListData.filter((tab: any) => tab.key === 'zapier')[0];

  useEffect(
    () => {
      if (generateKeyStatus === 'success') {
        setApiKey(generateKeyData?.payload?.api_key);
      }
    },
    // eslint-disable-next-line
    [generateKeyStatus]
  );

  useEffect(
    () => {
      if (zapierIntegrationData.is_connected) {
        onEnable();
      }
    }, // eslint-disable-next-line
    []
  );

  const revokeApiKey = () => {
    revokeMutateAsync(apiKey).then(() => {
      setApiKey('');
    });
  };

  const onFetchApiKey = () => {
    refetch().then((res: any) => {
      setApiKey(res?.data?.payload?.api_key);
    });
  };

  const onEnable = () => {
    setEnable(true);
    const widget = document.createElement('script');
    widget.type = 'text/javascript';
    widget.async = true;
    widget.src = 'https://zapier.com/apps/embed/widget.js?services=timeero&limit=8&html_id=zapier';
    const zapierCtn: any = document.getElementById('zapier');
    zapierCtn.appendChild(widget);
  };

  const copyApiKey = () => {
    navigator.clipboard.writeText(generateKeyData?.payload?.api_key);
    toaster.info({ description: 'Copied!' });
  };

  return (
    <>
      {!enable && (
        <Flex flexDirection="column">
          <Box
            padding="20px 40px"
            border="1px solid"
            borderColor="gray.400"
            background="white"
            textAlign="center"
            mb={6}
          >
            <AnimatedMascot type="barking" />
            <Text>{t('integrations:clickToEnable')}</Text>
          </Box>
          <Button ml="auto" onClick={onEnable}>
            {t('integrations:enable')}
          </Button>
        </Flex>
      )}
      <Flex flexDirection="column">
        {enable && apiKey && (
          <>
            <Flex alignItems="center" justifyContent="flex-end" margin="10px 0">
              <Text>{t('integrations:apiKey')}</Text>
              <Box
                fontSize="sm"
                fontWeight={600}
                ml={1}
                mr={1}
                border="1px solid"
                borderColor="gray.500"
                padding="5px 10px"
                borderRadius={6}
              >
                {apiKey || 'Loading...'}
              </Box>
              <Tooltip label="Copy" hasArrow bg="black">
                <IconButton
                  aria-label="Copy"
                  colorScheme="secondary"
                  icon={<Icon as={IconCopy} />}
                  size="sm"
                  onClick={copyApiKey}
                  borderRadius={6}
                />
              </Tooltip>
            </Flex>
            <Button
              type="button"
              colorScheme="primary"
              width="230px"
              ml="auto"
              mb={6}
              fontWeight="bold"
              background="rgba(250, 85, 85, 0.2)"
              color="rgba(250, 85, 85, 1)"
              _hover={{ bg: 'secondary.100' }}
              onClick={revokeApiKey}
              isLoading={revokeStatus === 'loading'}
            >
              {t('integrations:revokeApiKey')}
            </Button>
          </>
        )}
        {enable && !apiKey && (
          <>
            <Button
              type="button"
              colorScheme="primary"
              width="230px"
              ml="auto"
              mb={6}
              fontWeight="bold"
              background="rgba(39, 174, 96, 0.2)"
              color="rgba(39, 174, 96, 1)"
              _hover={{ bg: 'rgba(39, 174, 96, 0.5)' }}
              onClick={onFetchApiKey}
              isLoading={generateKeyStatus === 'loading'}
            >
              {t('integrations:generateApiKey')}
            </Button>
          </>
        )}
        <div id="zapier"></div>
      </Flex>
    </>
  );
};
export default Configuration;
